import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, LogIn, ShieldBan } from 'lucide-react';
import { useLogin } from '@/hooks/useApi';

const ADMIN_EMAIL = 'admin@opencardvault.com';

export default function LoginPage() {
  const navigate = useNavigate();
  const { mutate: login, isLoading } = useLogin();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});
  const [banInfo, setBanInfo] = useState<{ reason: string } | null>(null);

  const validate = () => {
    const newErrors: { email?: string; password?: string } = {};
    if (!email) newErrors.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) newErrors.email = 'Invalid email address';
    if (!password) newErrors.password = 'Password is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    login(
      { email, password },
      {
        onSuccess: () => navigate('/dashboard'),
        onError: (err: any) => {
          const data = err?.response?.data?.error;
          if (data?.code === 'ACCOUNT_BANNED') {
            setBanInfo({ reason: data.ban_reason || 'Violation of community guidelines' });
          }
        },
      }
    );
  };

  if (banInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-red-50 dark:from-red-950/40 to-zinc-100 dark:to-zinc-950 px-4">
        <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-red-200 dark:border-red-900 p-8 w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 mb-5">
            <ShieldBan className="h-7 w-7" />
          </div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-zinc-100 mb-2">Account Banned</h1>
          <p className="text-gray-600 dark:text-zinc-400 text-sm mb-1">Your account has been banned for:</p>
          <p className="text-red-600 dark:text-red-400 font-medium mb-6">"{banInfo.reason}"</p>
          <p className="text-sm text-gray-500 dark:text-zinc-500">
            To appeal this decision, contact the admin at{' '}
            <a href={`mailto:${ADMIN_EMAIL}`} className="text-brand-600 dark:text-brand-400 font-medium hover:underline">
              {ADMIN_EMAIL}
            </a>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-brand-50 dark:from-brand-900/40 to-accent-50 dark:to-zinc-950 px-4">
      <div className="bg-white dark:bg-zinc-900 rounded-2xl shadow-xl border border-gray-200 dark:border-zinc-700 p-8 w-full max-w-md">

        {/* Logo / Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-gradient-to-br from-brand-500 to-accent-500 text-white text-2xl mb-4 shadow-lg dark:shadow-black/40">
            🃏
          </div>
          <h1 className="text-2xl font-display font-bold text-gray-900 dark:text-zinc-100">Welcome back</h1>
          <p className="text-gray-500 dark:text-zinc-500 text-sm mt-1">Sign in to your account</p>
        </div>

        {/* Form */}
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              placeholder="you@example.com"
              className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-brand-400 transition-colors text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800 placeholder-gray-400 dark:placeholder-zinc-500 ${
                errors.email ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-zinc-700'
              }`}
            />
            {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
          </div>

          <div>
            <div className="flex justify-between items-center mb-1">
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300">Password</label>
              <Link to="/forgot-password" className="text-xs text-brand-600 dark:text-brand-400 hover:underline">
                Forgot password?
              </Link>
            </div>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                placeholder="••••••••"
                className={`w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2 focus:ring-brand-400 transition-colors pr-11 text-gray-900 dark:text-zinc-100 bg-white dark:bg-zinc-800 placeholder-gray-400 dark:placeholder-zinc-500 ${
                  errors.password ? 'border-red-400 bg-red-50 dark:bg-red-900/20' : 'border-gray-200 dark:border-zinc-700'
                }`}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-zinc-500 hover:text-gray-600 dark:hover:text-zinc-400 dark:text-zinc-400"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.password && <p className="text-red-500 text-xs mt-1">{errors.password}</p>}
          </div>

          <button
            onClick={handleSubmit}
            disabled={isLoading}
            className="btn-primary w-full flex items-center justify-center gap-2 py-3 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <span className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
            ) : (
              <LogIn className="h-4 w-4" />
            )}
            {isLoading ? 'Signing in...' : 'Sign In'}
          </button>
        </div>

        <p className="text-center text-sm text-gray-500 dark:text-zinc-500 mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-brand-600 dark:text-brand-400 font-medium hover:underline">
            Create one free
          </Link>
        </p>
      </div>
    </div>
  );
}